import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

// Admin token for API calls - set in .env.local as REACT_APP_ADMIN_TOKEN. No hardcoded
// fallback: a misconfigured build should fail admin auth loudly, not silently grant access
// via a value baked into source control.
const ADMIN_TOKEN = process.env.REACT_APP_ADMIN_TOKEN;

const getAdminHeaders = () => ({
  'X-Admin-Token': ADMIN_TOKEN
});

/**
 * Fetch the aggregated insights snapshot for a single user.
 * Backend endpoint: GET /api/admin/users/{user_id}/insights
 */
export const fetchUserInsights = createAsyncThunk('adminInsights/fetch', async (userId, { rejectWithValue }) => {
  try {
    const response = await axios.get(`/api/admin/users/${userId}/insights`, {
      headers: getAdminHeaders()
    });

    if (response.data.status === 'success') {
      return response.data.data;
    }
    return rejectWithValue(response.data.message || 'Failed to fetch user insights');
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || error.message);
  }
});

const initialState = {
  data: null,
  status: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed'
  error: null
};

const adminInsightsSlice = createSlice({
  name: 'adminInsights',
  initialState,
  reducers: {
    clearInsights: (state) => {
      state.data = null;
      state.status = 'idle';
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserInsights.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchUserInsights.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.data = action.payload;
      })
      .addCase(fetchUserInsights.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
        state.data = null;
      });
  }
});

export const { clearInsights } = adminInsightsSlice.actions;

export default adminInsightsSlice.reducer;
